import { LAW_BASE_PATH } from '#shared/constants'
import { numberToRoman } from '#shared/utils/roman-numerals'

export function useInternalLinkHandler(containerRef: Ref<HTMLElement | null>, scrollContainerKey: ContainerKey) {
  const params = useLawRouteParams()
  const { scrollToElement } = useScrollToElement('content')
  const { scrollToElement: scrollWithinContainer } = useScrollToElement(scrollContainerKey)
  const { showReference } = useLawReferencePanel()
  const containers = inject(diKeys.containers)

  const currentLawId = computed(() => params.value.lawId?.toLowerCase() || '')
  const currentNodeId = computed(() => params.value.nodeId?.toLowerCase() || '')

  function handleInteraction(event: MouseEvent | KeyboardEvent) {
    const link = (event.target as HTMLElement).closest('a')
    if (link && link.hasAttribute('href')) {
      const href = link.getAttribute('href')
      // Scroll inside the mounted container (not the window) for intra-document anchors,
      // restoring focus + URL hash after the scroll lands to match native anchor behaviour.
      if (href && href.startsWith('#') && href.length > 1) {
        event.preventDefault()
        event.stopPropagation()
        const targetId = href.slice(1)
        const target = containerRef.value?.querySelector(`#${CSS.escape(targetId)}`)
        if (target instanceof HTMLElement) {
          // Anchor targets are `<p>` elements, so use the clicked link text as the
          // screen reader label to provide section context.
          const tocLabel = link.textContent?.trim()
          if (tocLabel && !target.hasAttribute('aria-label')) {
            target.setAttribute('aria-label', tocLabel)
          }
          const targetWasFocusable = target.hasAttribute('tabindex')
          if (!targetWasFocusable) {
            target.setAttribute('tabindex', '-1')
          }
          target.setAttribute('data-anchor-focus', '')
          scrollWithinContainer(target, true, () => {
            target.focus({ preventScroll: true })
            history.replaceState(history.state, '', `${location.pathname}${location.search}#${targetId}`)
            const cleanup = () => {
              if (!targetWasFocusable) {
                target.removeAttribute('tabindex')
              }
              target.removeAttribute('data-anchor-focus')
              target.removeEventListener('blur', cleanup)
            }
            target.addEventListener('blur', cleanup, { once: true })
          })
        }
        return
      }
      if (href && href.startsWith(`/${LAW_BASE_PATH}/`)) {
        event.preventDefault()
        event.stopPropagation()
        const pathSegments = href.split('/').filter(Boolean).map(s => s.toLowerCase())

        if (pathSegments.length < 2 || pathSegments[0] !== LAW_BASE_PATH) {
          return
        }

        const targetLawId = pathSegments[1]
        if (!targetLawId) {
          return
        }
        const isSameLaw = targetLawId === currentLawId.value

        if (isSameLaw) {
          const relativePath = pathSegments.slice(2).join('/')
          const sectionId = `${currentLawId.value}/${relativePath}`
          const targetParagraph = pathSegments[pathSegments.length - 1]

          let targetElement = document.getElementById(sectionId)

          if (!targetElement && targetParagraph) {
            // We're using suffix matching because the reference ID's in the commentary text follow a flat path structure rather than a hierarchical one
            // Scope to content container to avoid matching elements from other laws (e.g., reference panel)
            const contentContainer = toValue(containers?.content)

            // Normalize paragraph IDs to remove the trailing hyphen-letter pattern if present
            const normalizedParagraph = targetParagraph.replace(/-([a-z])$/, '$1')
            targetElement = contentContainer?.querySelector(`[id$="/${normalizedParagraph}"]`) as HTMLElement | null

            // Fallback: Try matching with roman numeral suffix if the normalized version doesn't find a match (e.g., "kapittel-2" -> "kapittel-ii")
            if (!targetElement) {
              const arabicMatch = normalizedParagraph.match(/^(.+)-(\d+)$/)
              if (arabicMatch) {
                const romanVersion = `${arabicMatch[1]}-${numberToRoman(Number(arabicMatch[2]))}`
                targetElement = contentContainer?.querySelector(`[id$="/${romanVersion}"]`) as HTMLElement | null
              }
            }
          }

          if (targetElement) {
            scrollToElement(targetElement)

            const actualNodeId = targetElement.id
            const hierarchicalPath = actualNodeId.split('/').slice(1) // Remove lawId segment

            if (actualNodeId !== currentNodeId.value) {
              navigateTo({ name: 'law-by-id', params: { lovId: [currentLawId.value, ...hierarchicalPath] } })
            }
          }
          else {
            if (import.meta.dev) {
              console.warn(`Target element not found for "${targetParagraph}", falling back to flat path navigation`)
            }

            if (sectionId !== currentNodeId.value) {
              navigateTo({ name: 'law-by-id', params: { lovId: pathSegments.slice(1) } })
            }
          }
        }
        else {
          const fullPath = pathSegments.slice(1).join('/')
          showReference(fullPath, scrollContainerKey, link)
        }
      }
    }
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      const link = (event.target as HTMLElement).closest('a')
      if (link && link.hasAttribute('href')) {
        handleInteraction(event)
      }
    }
  }

  watchEffect((onCleanup) => {
    const container = containerRef.value
    if (container) {
      container.addEventListener('click', handleInteraction, { passive: false })
      container.addEventListener('keydown', handleKeyDown)

      onCleanup(() => {
        container.removeEventListener('click', handleInteraction)
        container.removeEventListener('keydown', handleKeyDown)
      })
    }
  })
}
